import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { IsNever, useSyncInitialAction } from '@direct-frontend/stdlib';

import { BaseFields, InferFieldsValue } from '../fields/types';
import { BaseValidationError } from '../types';

import { $internals, FormActionsMap, FormDeclaration } from './types';
import { useSubscription } from './useSubscription';
import { getFormsContext } from './FormsContext';

/** Пропсы компонента `Form` */
type FormProps<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
> = {
    /** Дочерние элементы, которые будут отрендерены после инициализации формы */
    children: ReactNode;
    /** Форма, которую необходимо проинициализировать */
    form: FormDeclaration<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >;
    /** Идентификатор формы */
    id: string;
    /** Начальное значение формы – используется только тогда, когда форма не проинициализирована никаким значением */
    initialValue?: InferFieldsValue<FormFields>;
} & IsNever<
    FormContextInput,
    {},
    {
        /** Значение контекста формы */
        context: FormContextInput;
    }
>;

/** Корневой компонент формы – инициализирует значение, управляет подписками и очищает данные формы */
export function Form<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>({
    children,
    form,
    id,
    initialValue,
    ...options
}: FormProps<
    FormName,
    FormFields,
    FormValidationError,
    FormContextInput,
    FormContext,
    FormActions,
    FormSubmitArg,
    FormSubmitReturnValue
>) {
    const instance = useMemo(() => form.getInstance(id), [form, id]);

    const shouldReinitializeOnMountRef = useRef<boolean>(true);
    const { shouldSaveSubscriptions } = getFormsContext();

    // Поле context опционально внутри условного типа – оператор in сужает его тип до `unknown`
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    const context = ('context' in options ? options.context : undefined) as FormContextInput;

    // Мемоизируем функцию инициализации, чтобы лишний раз не вызывать эффект внутри `useSyncInitialAction`:
    // – Если изменится form или initialValue, то функция вызовется заново, так как мы не знаем изменилась ли форма
    // – Если это та же самая форма, то от повторной инициализации защитит проверка внутри `instance.initialize`
    const initialize = useCallback(() => {
        instance[$internals].initialize(initialValue, context, {
            shouldReinitialize: shouldSaveSubscriptions && shouldReinitializeOnMountRef.current,
        });
        shouldReinitializeOnMountRef.current = false;
    }, [instance, initialValue, context, shouldReinitializeOnMountRef, shouldSaveSubscriptions]);

    const isInitialized = useSyncInitialAction(initialize);

    // При изменении параметров контекста обновляем его значение
    useEffect(() => {
        instance.setContext(context);
    }, [instance, context]);

    useSubscription(instance);

    return <>{instance[$internals].useIsFormInitialized() && isInitialized ? children : null}</>;
}
